import { formatDateShortDD } from '~/services/TimeService';

import { type ChecklistStatus, isFiniteNumber } from './checklistUtils';

export function getStatusLabel(status: ChecklistStatus) {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'requires approval':
      return 'Requires approval';
    case 'blocked':
      return 'Blocked';
    default:
      return 'Unknown';
  }
}

export function formatOptionalNumber(value?: number) {
  return isFiniteNumber(value) ? String(value) : 'N/A';
}

// "4 (2A2C)": the flattened total with the Salesforce breakdown behind it, when one exists.
export function formatCapacityWithBreakdown(total?: number, breakdown?: string) {
  const formattedTotal = formatOptionalNumber(total);

  return breakdown && breakdown !== formattedTotal ? `${formattedTotal} (${breakdown})` : formattedTotal;
}

export function formatMoney(value?: number | null, currencyCode = 'AUD') {
  return isFiniteNumber(value) ? `${currencyCode} ${value}` : 'N/A';
}

export function formatPrice(value: string | number | null | undefined, currencyCode = 'AUD') {
  if (value == null || value === '') {
    return '—';
  }
  return `${currencyCode} ${value}`;
}

export function formatContextDate(value?: string) {
  return value ? formatDateShortDD(value) : 'Not available';
}

export function formatBooleanDataPointValue({
  dataAvailable,
  enabled,
  unavailableValue = 'N/A',
}: {
  dataAvailable: boolean;
  enabled?: boolean;
  unavailableValue?: string;
}) {
  if (!dataAvailable) {
    return unavailableValue;
  }
  return enabled ? 'Yes' : 'No';
}

export function formatLoadedDataPointValue({ dataAvailable, loaded }: { dataAvailable: boolean; loaded: boolean }) {
  if (!dataAvailable) {
    return 'N/A';
  }
  return loaded ? 'Loaded' : 'Not loaded';
}

// Deal option names come through as e.g. "5n Duplex w/ Pool 6A" — capitalize the nights
// abbreviation for display ("5N Duplex...").
export function formatDealOptionName(name: string) {
  return name.replace(/^(\d+)n\b/i, '$1N');
}

export function getDealToggleLabel({ dealCount, dealsExpanded }: { dealCount: number; dealsExpanded: boolean }) {
  const dealNoun = dealCount === 1 ? 'deal option' : 'deal options';
  return `${dealsExpanded ? 'Hide' : 'View'} ${dealCount} ${dealNoun}`;
}

// Salesforce room size is free text; bare numbers are square metres.
export function formatRoomSize(value: string | null) {
  if (!value) {
    return undefined;
  }
  return /^\d+(\.\d+)?$/.test(value.trim()) ? `${value.trim()} sqm` : value;
}

const GUEST_TYPE_ABBREVIATIONS = [
  ['adults', 'A'],
  ['teenagers', 'T'],
  ['children', 'C'],
  ['infants', 'I'],
] as const;

// Salesforce-style occupancy text, e.g. "2A2C"; null and zero counts are omitted.
export function formatGuestTypeCounts(
  counts: Record<(typeof GUEST_TYPE_ABBREVIATIONS)[number][0], number | null> | null,
) {
  const parts = GUEST_TYPE_ABBREVIATIONS.flatMap(([key, abbreviation]) => {
    const count = counts?.[key];
    return isFiniteNumber(count) && count > 0 ? [`${count}${abbreviation}`] : [];
  });

  return parts.length > 0 ? parts.join('') : undefined;
}

// [2, 3, 4, 7] -> "ages 2–4, 7"; [5] -> "age 5".
export function formatAgeList(ages: Array<number>) {
  const sortedAges = [...new Set(ages)].sort((a, b) => a - b);
  const ranges: Array<[number, number]> = [];

  for (const age of sortedAges) {
    const lastRange = ranges[ranges.length - 1];
    if (lastRange && age === lastRange[1] + 1) {
      lastRange[1] = age;
    } else {
      ranges.push([age, age]);
    }
  }

  const ageRangesText = ranges.map(([from, to]) => (from === to ? String(from) : `${from}–${to}`)).join(', ');
  return `${sortedAges.length === 1 ? 'age' : 'ages'} ${ageRangesText}`;
}
